import Box from "@mui/material/Box";
import FormOptionCard, {
  FormOptionCardConfig,
} from "app/components/FormOptionCard";
import React from "react";
import { useDispatch } from "react-redux";

import {
  tyingKey,
  tyingLabels,
  useQuoteAccessories,
} from "../../../hooks/useQuoteAccessories";
import { quotesActions } from "../../../redux/slice";
import {
  QuoteStorageAccessoriesCard,
  QuoteTyingEquipmentCard,
  STRAP_TENSIONER_TYPE_OPTIONS,
} from "../../../types";
import AccessoriesTable from "./AccessoriesTable";
import {
  CardRow,
  StructureCard,
  StructureSection,
} from "./BoxStructureFields";
import { NumberField, SelectField } from "./FormFields";
import { useStyles } from "../styles";

const QUANTITY = tyingKey("FIELDS.QUANTITY");
const QUANTITY_OPTION = tyingKey("FIELDS.QUANTITY_OPTION");

const storageAccessoriesCards: Record<
  QuoteStorageAccessoriesCard,
  FormOptionCardConfig
> = {
  polesBasket: {
    titleKey: tyingKey("CARDS.POLES_BASKET"),
    fields: ["quantity"],
    quantityLabelKey: QUANTITY,
  },
  waterJerrycan: {
    titleKey: tyingKey("CARDS.WATER_JERRYCAN"),
    fields: ["quantity"],
    quantityLabelKey: QUANTITY,
  },
  palletJackMount: {
    titleKey: tyingKey("CARDS.PALLET_JACK_MOUNT"),
    layout: "horizontal",
    fields: ["radio", "note"],
    radioLabelKey: tyingKey("FIELDS.TYPE"),
    notePlaceholderKey: tyingKey("FIELDS.NOTE"),
    radioOptions: [
      { value: "FROM_SIDE", labelKey: tyingKey("OPTIONS.FROM_SIDE") },
      { value: "REGULAR", labelKey: tyingKey("OPTIONS.REGULAR") },
    ],
  },
};

const AccessoriesTying = () => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const data = useQuoteAccessories();
  const strap = data.tyingEquipment.strapTensioners;

  const patchTying = (
    key: QuoteTyingEquipmentCard,
    value: Partial<(typeof data.tyingEquipment)[QuoteTyingEquipmentCard]>
  ) =>
    dispatch(
      quotesActions.updateAccessories({
        section: "tyingEquipment",
        key,
        value,
      })
    );

  const quantityCard = (
    cardId: Exclude<QuoteTyingEquipmentCard, "strapTensioners">,
    titleKey: string,
    quantityLabelKey = QUANTITY,
    alignFields = false
  ) => {
    const item = data.tyingEquipment[cardId];

    return (
      <StructureCard
        key={cardId}
        enabled={item.enabled}
        titleKey={titleKey}
        alignFields={alignFields}
        onToggle={(enabled) => patchTying(cardId, { enabled })}
      >
        <NumberField
          labelKey={quantityLabelKey}
          value={item.quantity}
          onChange={(quantity) => patchTying(cardId, { quantity })}
          grow
        />
      </StructureCard>
    );
  };

  const renderStorageCard = (cardId: QuoteStorageAccessoriesCard) => (
    <FormOptionCard
      key={cardId}
      config={storageAccessoriesCards[cardId]}
      value={data.storageAccessories[cardId]}
      labels={tyingLabels}
      onChange={(value) =>
        dispatch(
          quotesActions.updateAccessories({
            section: "storageAccessories",
            key: cardId,
            value,
          })
        )
      }
    />
  );

  return (
    <>
      <StructureSection titleKey={tyingKey("SECTIONS.TYING")} plain>
        <CardRow>
          {quantityCard(
            "sideCableTensioner",
            tyingKey("CARDS.SIDE_CABLE_TENSIONER")
          )}
          <StructureCard
            enabled={strap.enabled}
            titleKey={tyingKey("CARDS.STRAP_TENSIONERS")}
            onToggle={(enabled) => patchTying("strapTensioners", { enabled })}
          >
            <Box
              className={`${styles.optionFieldsRow} ${styles.optionFieldsRowLtr}`}
            >
              <NumberField
                labelKey={QUANTITY}
                value={strap.quantity}
                onChange={(quantity) =>
                  patchTying("strapTensioners", { quantity })
                }
                narrow
              />
              <SelectField
                labelKey={tyingKey("FIELDS.TYPE")}
                value={strap.selectedOption}
                options={STRAP_TENSIONER_TYPE_OPTIONS}
                onChange={(selectedOption) =>
                  patchTying("strapTensioners", { selectedOption })
                }
                grow
              />
            </Box>
          </StructureCard>
        </CardRow>
        <CardRow columns={4}>
          {quantityCard(
            "frontCableTensioner",
            tyingKey("CARDS.FRONT_CABLE_TENSIONER"),
            QUANTITY,
            true
          )}
          {quantityCard(
            "recessedRings",
            tyingKey("CARDS.RECESSED_RINGS"),
            QUANTITY_OPTION,
            true
          )}
          {quantityCard(
            "apcRings",
            tyingKey("CARDS.APC_RINGS"),
            QUANTITY_OPTION,
            true
          )}
          {quantityCard(
            "additionalTyingHooks",
            tyingKey("CARDS.ADDITIONAL_TYING_HOOKS"),
            QUANTITY_OPTION,
            true
          )}
        </CardRow>
      </StructureSection>

      <StructureSection titleKey={tyingKey("SECTIONS.STORAGE")}>
        <AccessoriesTable />
        <Box className={styles.storageRow}>
          <Box className={styles.storageQuantityRow}>
            {renderStorageCard("waterJerrycan")}
            {renderStorageCard("polesBasket")}
          </Box>
          {renderStorageCard("palletJackMount")}
        </Box>
      </StructureSection>
    </>
  );
};

export default AccessoriesTying;
